import * as React from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Device from '../../models/device';
import DeviceRow from './deviceRow';
import TableHead, { Order } from '../../components/table/TableHead';
import DeviceTableToolbar, { DeviceStateFilter } from './deviceTableToolbar';
import { deviceFieldHeaders, deviceTableFields } from './devicefieldsConfig';
import Table from '../../components/table/Table';
import { filterDevices, getComparator } from '../../utils';
import { useContextSelector } from 'use-context-selector';
import { DevicesContext, DeviceStore } from './deviceContext';
import { UserPost } from '../../userFetch';

interface PropsTypes {
  userId: string;
  isSuperAdmin?: boolean;
}

export default function DevicesTable(props: PropsTypes) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(() => {
    const saved = sessionStorage.getItem('devicesTableRowsPerPage');
    return saved ? parseInt(saved, 10) : 100;
  });
  const [selected, setSelected] = React.useState<string[]>([]);
  const firstRowRef = React.useRef<HTMLTableRowElement>(null);
  const firstCommandInputRef = React.useRef<HTMLInputElement>(null);
  const bulkCommandInputRef = React.useRef<HTMLInputElement>(null);
  const isSelected = (serial_number: string) => selected.indexOf(serial_number) !== -1;
  const devices = useContextSelector(DevicesContext, (c) => c.state.devices as Record<string, DeviceStore>);
  const devicesDispatch = useContextSelector(DevicesContext, (c) => c.dispatch);
  const [devicesActiveStateFilter, setDevicesActiveStateFilter] = React.useState<DeviceStateFilter>(() => {
    const saved = sessionStorage.getItem('devicesActiveStateFilter');
    return (saved as DeviceStateFilter) || 'all';
  });
  React.useEffect(() => { sessionStorage.setItem('devicesActiveStateFilter', devicesActiveStateFilter); }, [devicesActiveStateFilter]);

  // Count devices flagged by the server's status rollup. Drives the muted/active
  // appearance of the "Issues" filter chip and is the source-of-truth for the
  // 'issues' filter mode below.
  const issuesCount = React.useMemo(
    () => Object.values(devices).filter((d: any) => d.status === 'warning' || d.status === 'critical').length,
    [devices]
  );
  // Same staleness rule as the active/inactive filter below — fleet-wide, not
  // affected by the text filter, so the chips read as "fleet health at a glance".
  const inactiveCount = React.useMemo(
    () => Object.values(devices).filter((d) => d.lastHeartbeatStale).length,
    [devices]
  );
  const activeCount = Object.keys(devices).length - inactiveCount;
  // Devices with a requested-but-not-completed command — the same set that drives the
  // toolbar's pulsing "Auto-refreshing commands" indicator. Joined to a string so the
  // selector re-renders on change (the reducer mutates ongoingCommand in place).
  const pendingKey = useContextSelector(DevicesContext, (c) => Object.keys(c.state.ongoingCommand).sort().join(','));
  const pendingSerials = React.useMemo(() => new Set(pendingKey ? pendingKey.split(',') : []), [pendingKey]);
  const pendingCount = pendingSerials.size;

  // Only offer filters that would change the list: All always; Active/Inactive only
  // when the fleet is split; Issues only when there are some. Alt+A cycles these.
  const visibleFilters = React.useMemo<(DeviceStateFilter)[]>(() => {
    const f: (DeviceStateFilter)[] = ['all'];
    if (inactiveCount > 0 && activeCount > 0) f.push('inactive', 'active');
    if (issuesCount > 0) f.push('issues');
    if (pendingCount > 0) f.push('pending');
    return f;
  }, [inactiveCount, activeCount, issuesCount, pendingCount]);
  // A saved filter whose radio is now hidden falls back to All (but not before devices load,
  // or the saved choice would be wiped on every page open).
  React.useEffect(() => {
    if (Object.keys(devices).length > 0 && !visibleFilters.includes(devicesActiveStateFilter)) setDevicesActiveStateFilter('all');
  }, [devices, visibleFilters, devicesActiveStateFilter]);


  const [order, setOrder] = React.useState<Order>(() => {
    const savedOrder = sessionStorage.getItem('devicesTableOrder');
    return (savedOrder as Order) || 'asc';
  });

  const [orderBy, setOrderBy] = React.useState<keyof Device>(() => {
    const savedOrderBy = sessionStorage.getItem('devicesTableOrderBy');
    return (savedOrderBy as keyof Device) || 'hostname';
  });

  const [filter, setFilter] = React.useState<string | undefined>(() => {
    // Deep link: /dashboard?device=<serial|label|hostname> lands on that device
    // with the list already filtered — how other apps (EngramCTX Ask) cite a
    // device. Takes precedence over the remembered filter for this visit.
    const linked = new URLSearchParams(window.location.search).get('device');
    if (linked) return linked.toLowerCase();
    const savedFilter = sessionStorage.getItem('devicesTableFilter');
    return savedFilter || undefined;
  });

  const filteredRows = React.useMemo(
    () => (filterDevices(Object.values(devices), filter) as DeviceStore[])
      .filter((device) => devicesActiveStateFilter === 'all'
        || (devicesActiveStateFilter === 'active' && !device.lastHeartbeatStale)
        || (devicesActiveStateFilter === 'inactive' && device.lastHeartbeatStale)
        || (devicesActiveStateFilter === 'issues' && (device.status === 'warning' || device.status === 'critical'))
        || (devicesActiveStateFilter === 'pending' && pendingSerials.has(device.serial_number))),
    [devices, filter, devicesActiveStateFilter, pendingSerials]
  );

  const visibleRows = React.useMemo(
    () => filteredRows
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows],
  );

  const selectableRowCount = React.useMemo(
    () => visibleRows.filter((device) => !device.lastHeartbeatStale).length,
    [visibleRows]
  );

  const handleRequestSort = React.useCallback((_event: React.MouseEvent<unknown>, property: keyof Device, ctrlKey = false) => {
    const isAsc = orderBy === property && order === 'asc';

    // Define fields that should be descending by default (newest/highest first)
    const defaultDescendingFields = [
      'created', 'heartbeat_received', 'command.requested', 'command.fetched',
      'cpu_temperature', 'cpu_temperature_max', 'memory_current', 'memory_maximum'
    ];

    const isSameColumn = orderBy === property;

    let newOrder: Order;

    if (ctrlKey) {
      // When Ctrl is pressed, invert the normal behavior
      if (isSameColumn) {
        newOrder = isAsc ? 'asc' : 'desc';
      } else {
        // Initial sort for new column - invert the default
        newOrder = defaultDescendingFields.includes(property as string) ? 'asc' : 'desc';
      }
    } else {
      if (isSameColumn) {
        newOrder = isAsc ? 'desc' : 'asc';
      } else {
        // Initial sort for new column - use the default for this field type
        newOrder = defaultDescendingFields.includes(property as string) ? 'desc' : 'asc';
      }
    }

    setOrder(newOrder);
    setOrderBy(property);

    sessionStorage.setItem('devicesTableOrder', newOrder);
    sessionStorage.setItem('devicesTableOrderBy', property as string);
  }, [order, orderBy]);

  const handleRowClick = React.useCallback((serial_number: string) => {
    const selectedIndex = selected.indexOf(serial_number);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, serial_number);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  }, [selected]);

  const handleSelectAllClick = React.useCallback((_event: React.ChangeEvent<HTMLInputElement>) => {
    const selectableDevices = visibleRows.filter((device) => !device.lastHeartbeatStale);
    const allSelectableSelected = selectableDevices.length > 0 &&
      selectableDevices.every(device => selected.includes(device.serial_number));

    if (allSelectableSelected) {
      setSelected([]);
    } else {
      const newSelected = selectableDevices.map((n) => n.serial_number);
      setSelected(newSelected);
    }
  }, [visibleRows, selected]);

  const handleChangePage = React.useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    sessionStorage.setItem('devicesTableRowsPerPage', String(value));
  }, []);

  const handleChangeFilter = React.useCallback((filter?: string) => {
    const lowercaseFilter = filter?.toLowerCase();
    setFilter(lowercaseFilter);

    // Store filter in sessionStorage for persistence
    if (lowercaseFilter) {
      sessionStorage.setItem('devicesTableFilter', lowercaseFilter);
    } else {
      sessionStorage.removeItem('devicesTableFilter');
    }
  }, []);

  const handleBulkAction = React.useCallback(async (action: (serial_number: string, device?: Device) => Promise<Response>) => {
    if (selected.length === 0) return;

    for (let i = 0; i < selected.length; i++) {
      const serialNumber = selected[i];
      try {
        await action(serialNumber, devices[serialNumber]);
      } catch (error) {
        console.error(`Error sending command to device ${serialNumber}:`, error);
      }
      if (i < selected.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    devicesDispatch({
      type: 'RELOAD_DEVICES'
    });

    setSelected([]);
  }, [selected, devices, devicesDispatch]);

  // Bulk fullupdate goes through the server-side per-owner queue instead of the
  // fire-and-forget 500ms loop above. The server serializes one device at a time
  // per owner so concurrent downloads don't saturate the LAN / cache peers / S3.
  const handleBulkFullupdate = React.useCallback(async (payload: string) => {
    if (selected.length === 0) return;
    try {
      const response = await UserPost('/api/devices/bulk-command', {
        serials: selected,
        operation: 'fullupdate',
        payload: payload || '',
      });
      if (!response.ok) {
        const text = await response.text();
        console.error('Bulk fullupdate request failed:', response.status, text);
      }
    } catch (e) {
      console.error('Bulk fullupdate request error:', e);
    }
    setSelected([]);
  }, [selected]);

  const handleExpandAll = React.useCallback(() => {
    visibleRows.forEach((device) => {
      sessionStorage.setItem(`drawer_${device.serial_number}`, 'true');
    });
    // Force re-render by updating a dummy state or use window location reload
    window.location.reload();
  }, [visibleRows]);

  const handleCollapseAll = React.useCallback(() => {
    visibleRows.forEach((device) => {
      sessionStorage.setItem(`drawer_${device.serial_number}`, 'false');
    });
    // Force re-render
    window.location.reload();
  }, [visibleRows]);

  // Auto-expand drawer when filter results in exactly one device
  React.useEffect(() => {
    if (filter && filteredRows.length === 1) {
      const device = filteredRows[0];
      const storageKey = `drawer_${device.serial_number}`;
      const currentState = sessionStorage.getItem(storageKey);

      // Only set and reload if it's not already expanded
      if (currentState !== 'true') {
        sessionStorage.setItem(storageKey, 'true');
        window.location.reload();
      }
    }
  }, [filter, filteredRows]);

  // Pagination calculation
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Alt+A to cycle through the visible filters (All → Inactive → Active → Issues → All)
      if (event.altKey && event.code === 'KeyA') {
        event.preventDefault();
        setDevicesActiveStateFilter(prev => visibleFilters[(visibleFilters.indexOf(prev) + 1) % visibleFilters.length]);
      }

      // Alt+C to focus bulk command input (when selected) or first device command input
      if (event.altKey && event.code === 'KeyC') {
        event.preventDefault();

        if (selected.length > 0 && bulkCommandInputRef.current) {
          bulkCommandInputRef.current.focus();
        } else if (firstCommandInputRef.current) {
          firstCommandInputRef.current.focus();
        } else {
          const inputs = document.querySelectorAll('input[data="device-command-input-column"]') as NodeListOf<HTMLInputElement>;
          if (inputs.length > 0) {
            inputs[0].focus();
          } else if (firstRowRef.current) {
            firstRowRef.current.click();

            setTimeout(() => {
              if (firstCommandInputRef.current) {
                firstCommandInputRef.current.focus();
              }
            }, 500);
          }
        }
      }

      // Alt+X to toggle expand/collapse all
      if (event.altKey && event.code === 'KeyX') {
        event.preventDefault();

        // Check if any devices are expanded
        const anyExpanded = visibleRows.some((device) => {
          return sessionStorage.getItem(`drawer_${device.serial_number}`) === 'true';
        });

        if (anyExpanded) {
          handleCollapseAll();
        } else {
          handleExpandAll();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [firstRowRef, firstCommandInputRef, bulkCommandInputRef, selected, visibleRows, visibleFilters, handleSelectAllClick, handleExpandAll, handleCollapseAll]);

  return (
    <Table
      rowsNumber={filteredRows.length}
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      paginationLabel={
        filteredRows.length === Object.keys(devices).length
          ? `Total: ${Object.keys(devices).length}`
          : `Showing ${filteredRows.length} of ${Object.keys(devices).length}`}
      toolbar={
        <DeviceTableToolbar
          numSelected={props.isSuperAdmin ? selected.length : 0}
          onRequestFilter={handleChangeFilter}
          onExecuteAction={handleBulkAction as (action: (serial_number: string, device: Device) => Promise<never>) => void}
          onBulkFullupdate={handleBulkFullupdate}
          filterValue={filter}
          activeState={devicesActiveStateFilter}
          onActiveStateChange={setDevicesActiveStateFilter}
          issuesCount={issuesCount}
          pendingCount={pendingCount}
          activeCount={activeCount}
          inactiveCount={inactiveCount}
          visibleFilters={visibleFilters}
          isSuperAdmin={props.isSuperAdmin}
          bulkInputRef={bulkCommandInputRef}
        />
      }
      head={
        <TableHead
          numSelected={props.isSuperAdmin ? selected.length : 0}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={props.isSuperAdmin ? handleSelectAllClick : undefined}
          onRequestSort={handleRequestSort}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          rowCount={selectableRowCount}
          tableFields={props.isSuperAdmin ? deviceTableFields : deviceTableFields.filter(f => f !== undefined)}
          fieldHeaders={deviceFieldHeaders}
        />}
    >{
        visibleRows.map((device, index) => {
          const isItemSelected = isSelected(device.serial_number);
          const labelId = `enhanced-table-checkbox-${index}`;

          return (
            <DeviceRow
              key={device.serial_number}
              serialNumber={device.serial_number}
              userId={props.userId}
              isSuperAdmin={props.isSuperAdmin}
              onCheck={handleRowClick}
              isSelected={isItemSelected}
              checkboxID={labelId}
              commandInputRef={index === 0 ? (ref) => { firstCommandInputRef.current = ref; } : undefined}
              tableRowProps={index === 0 ? { ref: (ref) => { firstRowRef.current = ref; } } : undefined}
            />
          );
        })}
      {emptyRows > 0 && (
        <TableRow
          style={{
            height: 44 * emptyRows,
          }}
        >
          <TableCell colSpan={17} />
        </TableRow>
      )}
    </Table>
  );
}